import { Link } from 'react-router-dom';
import { requestChangeHref } from '../../utils/request-change';
import { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Heading,
  BodyText,
  Button,
  MetricCard,
  MetricCardGrid,
  RegulatoryHeatMap,
  Sheet,
  Badge,
} from '@aaimplatform/ui';
import type { BadgeProps } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/regulatory-readiness';
import {
  RefreshCw as RefreshIcon,
  Download as DownloadIcon,
  FileText as FileTextIcon,
  AlertTriangle as WarningIcon,
  ShieldAlert as ShieldAlertIcon,
} from 'lucide-react';
import { useHeatMap, useDownloadReport, useEntityConfigs } from '../../hooks/useRegulatoryReadiness';
import { useAuthStore } from '../../stores/auth';

/**
 * The apex tenant id ("Aaim, Inc.") -- mirrored from the canonical resolver
 * in @aaimplatform/database/security. Platform admins (e.g., rob@aaim.com)
 * resolve to this id. An apex session does not read an institution's
 * assessment here; it is pointed to Regulatory Readiness in the Aaim operator
 * console. FI-tenant operators see their own institution's readiness
 * assessment.
 */
const APEX_TENANT_ID = '00000000-0000-0000-0000-000000aaaaaa';

function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

type ReadinessSignal = components['schemas']['ReadinessSignalValue'];

const SIGNAL_LABELS: Record<ReadinessSignal, string> = {
  GREEN: 'Green',
  YELLOW: 'Yellow',
  RED: 'Red',
};

/**
 * What each signal says about the perfection path, shared by the summary
 * cards and the cell detail sheet. The signal grades perfection readiness
 * (certainty, priority, feasibility, asset risk), not compliance with a rule
 * (r3 fi-g6-3).
 */
const SIGNAL_CAPTIONS: Record<ReadinessSignal, string> = {
  GREEN: 'Perfection path established',
  YELLOW: 'Attention needed',
  RED: 'No sufficient perfection path',
};

const SIGNAL_BADGE_VARIANTS: Record<ReadinessSignal, NonNullable<BadgeProps['variant']>> = {
  GREEN: 'success',
  YELLOW: 'warning',
  RED: 'error',
};

/**
 * Regulatory Readiness page -- /audit/regulatory-readiness
 *
 * Displays the regulatory readiness heat map for the active FI tenant.
 * Data is served by platform-admin-api at /api/admin/regulatory-readiness/*
 * (proxied from /api/v1/regulatory/*).
 *
 * Sprint B Lane B2 (2026-05-17): admin-portal regulatory-readiness defaults
 * to "your own institution's readiness" (per `.claude/rules/multi-tenant-
 * request-shape.md`).
 *
 * Apex (platform-admin) sessions read no institution's assessment here (GH
 * #678, operator ruling Q1a, 2026-10-23). The cross-institution picker
 * lives on platform-admin (Regulatory Readiness, "Institution profile"), so
 * an apex session sees one card that points there, as text: no link is built
 * and no env var is read. The apex branch mounts no query.
 *
 * FI-tenant sessions fail closed on ambiguity (run 2026-09-27, fi-g6-2). The
 * page auto-selects an entity configuration only when the server returns
 * exactly one. GET /entity-configs was not tenant-filtered, and the page
 * used to take configs[0] of the full list, which presented another
 * institution's assessment under "your institution". With zero or several
 * configurations the page names the condition instead of picking one. The
 * EntityConfigSummary contract carries no tenantId, so the page cannot pick
 * the tenant's own configuration out of a longer list; the server's tenant
 * filter is what makes the single-row case the tenant's own.
 */
export function RegulatoryReadinessPage() {
  const user = useAuthStore((s) => s.user);
  // Platform admin without active impersonation resolves to apex; the
  // session also carries apex when an SSO platform admin lands without a
  // tenantId claim. Both cases get the operator-console pointer.
  const isApexContext = !user?.tenantId || user.tenantId === APEX_TENANT_ID;

  return isApexContext ? <ApexReadinessPointer /> : <TenantReadiness />;
}

/**
 * The apex session's whole page: regulatory readiness across institutions
 * lives in the Aaim operator console (platform.aaim.com). Text pointer only.
 */
function ApexReadinessPointer() {
  return (
    <Card className="p-12 text-center">
      <ShieldAlertIcon className="text-tertiary mb-4 mx-auto" size={48} />
      <Heading level={2} size="card" className="font-sans mb-2">
        Regulatory readiness across institutions is in the Aaim operator console
      </Heading>
      <BodyText variant="ui" className="text-tertiary max-w-md mx-auto">
        This page shows an institution’s own readiness assessment to that institution’s staff. To
        review the assessment for any institution, open Regulatory Readiness in the Aaim operator
        console on platform.aaim.com.
      </BodyText>
    </Card>
  );
}

/** An FI-tenant session: the readiness assessment for its own institution. */
function TenantReadiness() {
  const [selectedEntityId, setSelectedEntityId] = useState<string | undefined>(undefined);
  const [selectedCell, setSelectedCell] = useState<{
    jurisdictionCode: string;
    assetClassGroupCode: string;
  } | null>(null);

  const { data: heatMapData, isLoading, error, refetch } = useHeatMap(selectedEntityId);
  // The list endpoint returns the entity configurations in one unpaginated
  // read; the page uses it only to select the tenant's own configuration.
  const {
    data: entityConfigsData,
    isLoading: entityConfigsLoading,
    error: entityConfigsError,
  } = useEntityConfigs('');
  const downloadReport = useDownloadReport();

  const tenantConfigs = entityConfigsData?.configs;

  useEffect(() => {
    if (selectedEntityId) return;
    if (tenantConfigs && tenantConfigs.length === 1) {
      setSelectedEntityId(tenantConfigs[0].id);
    }
  }, [tenantConfigs, selectedEntityId]);

  const handleDownloadExecutiveSummary = () => {
    if (!selectedEntityId) return;
    downloadReport.mutate({
      type: 'executive-summary',
      entityConfigId: selectedEntityId,
    });
  };

  const handleDownloadFullAnalysis = () => {
    if (!selectedEntityId) return;
    downloadReport.mutate({
      type: 'readiness-analysis',
      entityConfigId: selectedEntityId,
    });
  };

  // Rows arrive ordered by postal code but display full names, which read out
  // of order (Alaska before Alabama). Sort by the name the reader sees.
  const sortedRows = useMemo(
    () =>
      heatMapData
        ? [...heatMapData.rows].sort((a, b) => a.jurisdictionName.localeCompare(b.jurisdictionName, 'en-US'))
        : [],
    [heatMapData],
  );

  // The cell the detail sheet shows (GH #1965). A heat map cell carries its
  // signal only; the rationale and footnote read here.
  const cellDetail = useMemo(() => {
    if (!selectedCell || !heatMapData) return null;
    const row = heatMapData.rows.find((r) => r.jurisdictionCode === selectedCell.jurisdictionCode);
    const cell = row?.cells.find((c) => c.assetClassGroupCode === selectedCell.assetClassGroupCode);
    const group = heatMapData.assetClassGroups.find(
      (g) => g.groupCode === selectedCell.assetClassGroupCode,
    );
    if (!row || !cell || !group) return null;
    return { jurisdictionName: row.jurisdictionName, groupName: group.groupName, cell };
  }, [selectedCell, heatMapData]);

  // What an FI-tenant session sees before an assessment is selected.
  const tenantEmptyState: { title: string; body: string } | null = (() => {
    if (selectedEntityId) return null;
    if (entityConfigsError) {
      return {
        title: 'Readiness configuration could not be read',
        body: `The request for your institution's readiness configuration failed: ${entityConfigsError.message}`,
      };
    }
    if (entityConfigsLoading || !tenantConfigs || tenantConfigs.length === 1) {
      // One configuration is selected by the effect above on the next commit.
      return { title: 'Loading your readiness assessment', body: 'Reading your institution’s readiness configuration.' };
    }
    if (tenantConfigs.length === 0) {
      return {
        title: 'No readiness assessment is configured',
        body: 'No regulatory readiness configuration is recorded for your institution. Request one and we will set it up.',
      };
    }
    return {
      title: 'Your readiness assessment could not be identified',
      body: `${tenantConfigs.length} readiness configurations were returned and none is marked as your institution’s, so none is shown. Request a correction and we will resolve it.`,
    };
  })();

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <p className="text-sm font-sans text-tertiary">
            Collateral perfection readiness for your institution, by U.S. jurisdiction and asset class.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selectedEntityId && (
            <>
              <Button variant="secondary" onClick={() => void refetch()} disabled={isLoading}>
                <RefreshIcon className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} size={20} />
                Refresh
              </Button>
              <Button
                variant="secondary"
                onClick={handleDownloadExecutiveSummary}
                disabled={downloadReport.isPending}
              >
                <DownloadIcon className="mr-2" size={20} />
                Executive Summary
              </Button>
              <Button
                variant="primary"
                onClick={handleDownloadFullAnalysis}
                disabled={downloadReport.isPending}
              >
                <FileTextIcon className="mr-2" size={20} />
                Full Analysis
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Empty state when no entity selected. FI-tenant operators see no
          picker; the page auto-loads their institution's entity configuration
          when exactly one is returned. */}
      {!selectedEntityId && (
        <Card className="p-12 text-center">
          <ShieldAlertIcon className="text-tertiary mb-4 mx-auto" size={48} />
          <Heading level={2} size="card" className="font-sans mb-2">
            {tenantEmptyState?.title}
          </Heading>
          <BodyText variant="ui" className="text-tertiary max-w-md mx-auto">
            {tenantEmptyState?.body}
          </BodyText>
          {tenantConfigs && tenantConfigs.length !== 1 && !entityConfigsError && (
            <Link
              to={requestChangeHref({
                surface: 'Regulatory readiness',
                item:
                  tenantConfigs.length === 0
                    ? 'Set up a regulatory readiness configuration for my institution'
                    : 'Identify my institution’s regulatory readiness configuration',
              })}
              className="inline-block mt-4 text-sm font-sans text-accent hover:underline"
            >
              Request a correction
            </Link>
          )}
        </Card>
      )}

      {/* Error state */}
      {selectedEntityId && error && (
        <Card className="p-6 text-center">
          <WarningIcon className="text-error mb-2 mx-auto" size={32} />
          <BodyText variant="ui" className="text-error mb-4">
            Failed to load regulatory readiness data
          </BodyText>
          <BodyText variant="ui" className="text-tertiary text-sm mb-4">
            {error.message}
          </BodyText>
          <Button variant="primary" onClick={() => void refetch()}>
            <RefreshIcon className="mr-2" size={20} />
            Retry
          </Button>
        </Card>
      )}

      {/* Loading state */}
      {selectedEntityId && isLoading && !heatMapData && (
        <Card className="p-6 text-center">
          <BodyText variant="ui" className="text-tertiary">
            Loading heat map...
          </BodyText>
        </Card>
      )}

      {/* Heat map and summary when data loaded */}
      {selectedEntityId && heatMapData && !error && (
        <>
          {/* Summary Stats -- the same MetricCard the sibling Compliance
              pages use; tone rides on the change line, captioned from
              SIGNAL_CAPTIONS. */}
          <MetricCardGrid className="mb-6">
            <MetricCard
              label="Assessments"
              value={String(heatMapData.summary.totalCells)}
              description={`${plural(sortedRows.length, 'jurisdiction')} by ${plural(heatMapData.assetClassGroups.length, 'asset class', 'asset classes')}`}
            />
            <MetricCard
              label="Green"
              value={String(heatMapData.summary.greenCount)}
              change={SIGNAL_CAPTIONS.GREEN}
              changeType="positive"
            />
            <MetricCard
              label="Yellow"
              value={String(heatMapData.summary.yellowCount)}
              change={SIGNAL_CAPTIONS.YELLOW}
              changeType="neutral"
            />
            <MetricCard
              label="Red"
              value={String(heatMapData.summary.redCount)}
              change={SIGNAL_CAPTIONS.RED}
              changeType="negative"
            />
          </MetricCardGrid>

          {/* Heat Map. A cell shows its signal only; selecting one opens the
              detail sheet below with its rationale (GH #1965). The heat map
              scrolls its matrix inside its own overflow wrapper on a narrow
              viewport. */}
          <Card className="p-4">
            <RegulatoryHeatMap
              entityConfigName={heatMapData.entityConfigName}
              generatedAt={heatMapData.generatedAt}
              assetClassGroups={heatMapData.assetClassGroups}
              rows={sortedRows}
              summary={heatMapData.summary}
              onCellClick={(jurisdictionCode, assetClassGroupCode) =>
                setSelectedCell({ jurisdictionCode, assetClassGroupCode })
              }
            />
          </Card>
        </>
      )}

      <Sheet
        open={cellDetail !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedCell(null);
        }}
        side="right"
        size="md"
        title={cellDetail?.jurisdictionName}
        description={cellDetail?.groupName}
      >
        {cellDetail && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={SIGNAL_BADGE_VARIANTS[cellDetail.cell.signal]} size="small">
                {SIGNAL_LABELS[cellDetail.cell.signal]}
              </Badge>
              <BodyText variant="ui" className="text-sm text-secondary">
                {SIGNAL_CAPTIONS[cellDetail.cell.signal]}
              </BodyText>
            </div>
            <div>
              <p className="text-label-xs font-sans font-semibold uppercase tracking-widest text-tertiary mb-1">
                Rationale
              </p>
              <BodyText variant="ui" className="text-primary">
                {cellDetail.cell.signalRationale}
                {cellDetail.cell.footnoteRef && (
                  <sup className="ml-0.5 text-label-xs font-semibold">{cellDetail.cell.footnoteRef}</sup>
                )}
              </BodyText>
            </div>
          </div>
        )}
      </Sheet>

      {/* Data provenance footer */}
      <div className="mt-8 pt-4 border-t border-recessed">
        <BodyText variant="ui" className="text-xs text-tertiary">
          Regulatory rules and jurisdiction profiles are sourced from Aaim's
          canonical regulatory database. This view is read-only: the rules are shared across every
          institution, so a correction is a change to the database, not to your
          copy of it.{' '}
          {/* GH #1962: was "contact compliance@aaim.com" -- a dead end. */}
          <Link
            to={requestChangeHref({
              surface: 'Regulatory readiness',
              item: 'Addition or correction to a regulatory rule / jurisdiction profile',
            })}
            className="text-accent hover:underline"
          >
            Request a correction
          </Link>
          .
        </BodyText>
      </div>
    </div>
  );
}
